// admin/src/components/Header.jsx
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Colors } from "../constants/Colors";
import { useAuth } from "../context/AuthContext";
import api from "../services/api";
import logo from "../assets/images/ArkilaLogo.png";
import ProfileModal from "./ProfileModal";
import EditProfileModal from "./EditProfileModal";
import "../styles/header.css";

const NOTIFICATIONS_POLL_MS = 30000;

/* ------------------------------------------------------------------ */
/* Small helper: derive initials for avatar fallback                  */
/* ------------------------------------------------------------------ */
const getInitials = (name = "") => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const Header = ({
  toggleSidebar,
  onOpenModal,
  unreadCount: unreadCountProp,
  setNotificationsOpen,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [unreadCount, setUnreadCount] = useState(unreadCountProp || 0);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const pollTimerRef = useRef(null);
  const abortRef = useRef(null);
  const dropdownRef = useRef(null);

  const navigate = useNavigate();
  const { admin, logout, updateProfile } = useAuth();

  // Fallback user — but flagged so we can render gracefully
  const user = admin ?? {
    name: "Admin",
    email: "admin@arkila.com",
    role: "Admin",
    joined: "—",
  };

  /* -------------------------------------------------------------- */
  /* Unread-count polling                                            */
  /* -------------------------------------------------------------- */
  const fetchUnreadCount = useCallback(async () => {
    const token = localStorage.getItem("arkila_admin_token");
    if (!token) return;

    // Cancel prior in-flight request
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const { data } = await api.get("/admin/notifications", {
        params: { limit: 1, unread_only: true },
        signal: controller.signal,
      });
      if (data?.success) {
        setUnreadCount(data.unread_count || 0);
      }
    } catch (err) {
      if (err.name === "CanceledError" || err.code === "ERR_CANCELED") return;
      if (err.response?.status === 401) {
        // Token expired — stop polling
        if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      }
      // Otherwise silent fail
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("arkila_admin_token");
    if (!token) return;

    fetchUnreadCount();
    pollTimerRef.current = setInterval(fetchUnreadCount, NOTIFICATIONS_POLL_MS);

    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        fetchUnreadCount();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      if (abortRef.current) abortRef.current.abort();
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [fetchUnreadCount]);

  // Adopt parent-provided count when it changes
  useEffect(() => {
    if (typeof unreadCountProp === "number") {
      setUnreadCount(unreadCountProp);
    }
  }, [unreadCountProp]);

  /* -------------------------------------------------------------- */
  /* Close dropdown on outside click or Escape                       */
  /* -------------------------------------------------------------- */
  useEffect(() => {
    if (!showDropdown) return;

    const handleKey = (e) => {
      if (e.key === "Escape") setShowDropdown(false);
    };
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("keydown", handleKey);
    document.addEventListener("mousedown", handleClick);
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.removeEventListener("mousedown", handleClick);
    };
  }, [showDropdown]);

  /* -------------------------------------------------------------- */
  /* Handlers                                                        */
  /* -------------------------------------------------------------- */
  const handleProfileClick = () => {
    setShowProfileModal(true);
    setShowDropdown(false);
  };

  const handleSaveProfile = async (updatedUser) => {
    if (updateProfile) {
      await updateProfile(updatedUser);
      return;
    }
    // eslint-disable-next-line no-console
    console.warn("AuthContext has no updateProfile(); edit was not persisted.");
  };

  const handleLogout = async () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);

    try {
      await logout();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Logout error:", error);
    } finally {
      setShowDropdown(false);
      setIsLoggingOut(false);
      navigate("/login", { replace: true });
    }
  };

  const handleBellClick = () => {
    setNotificationsOpen?.((prev) => !prev);
    // Refresh badge count immediately
    fetchUnreadCount();
  };

  /* -------------------------------------------------------------- */
  /* Render                                                          */
  /* -------------------------------------------------------------- */
  return (
    <header
      className="admin-header sticky-top"
      style={{
        "--header-bg": Colors.WHITE,
        "--header-border": Colors.LIGHT_GRAY,
        "--header-text-dark": Colors.TEXT_DARK,
        "--header-text-light": Colors.TEXT_LIGHT,
        "--header-accent": Colors.PRIMARY,
        "--header-secondary": Colors.SECONDARY_BLUE,
        "--header-danger": Colors.RED,
      }}
    >
      <div className="admin-header-row">
        {/* Left */}
        <div className="admin-header-left">
          <button
            className="admin-header-menu-btn d-lg-none"
            type="button"
            onClick={toggleSidebar}
            aria-label="Open menu"
          >
            <i className="bi bi-list fs-4" />
          </button>
          <div className="d-none d-lg-block">
            <h4 className="admin-header-title">Arkila Admin</h4>
            <small className="admin-header-subtitle">
              Dashboard Management
            </small>
          </div>
        </div>

        {/* Right */}
        <div className="admin-header-right">
          <button
            type="button"
            className="admin-header-bell"
            onClick={handleBellClick}
            aria-label={
              unreadCount > 0
                ? `Notifications, ${unreadCount} unread`
                : "Notifications"
            }
          >
            <i className="bi bi-bell fs-5" />
            {unreadCount > 0 && (
              <span className="admin-header-badge">
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
          </button>

          <div className="admin-header-profile" ref={dropdownRef}>
            <button
              type="button"
              className="admin-header-profile-btn"
              onClick={() => setShowDropdown((prev) => !prev)}
              aria-haspopup="true"
              aria-expanded={showDropdown}
              aria-controls="admin-header-menu"
            >
              <img
                src={admin?.avatar || logo}
                alt=""
                className="admin-header-avatar"
                onError={(e) => {
                  // Fallback to initials if image fails
                  e.currentTarget.style.display = "none";
                }}
              />
              <span className="admin-header-username d-none d-md-inline">
                {user.name}
              </span>
              <i className="bi bi-chevron-down admin-header-caret" />
            </button>

            <ul
              id="admin-header-menu"
              className={`admin-header-dropdown${showDropdown ? " show" : ""}`}
              role="menu"
            >
              <li role="none">
                <a
                  role="menuitem"
                  className="admin-header-dropdown-item"
                  href="#!"
                  onClick={handleProfileClick}
                >
                  <i className="bi bi-person me-2" />
                  Profile
                </a>
              </li>
              <li role="none">
                <hr className="admin-header-divider" />
              </li>
              <li role="none">
                <a
                  role="menuitem"
                  className="admin-header-dropdown-item admin-header-dropdown-item--danger"
                  href="#!"
                  onClick={handleLogout}
                  aria-disabled={isLoggingOut}
                  style={
                    isLoggingOut
                      ? { opacity: 0.6, pointerEvents: "none" }
                      : undefined
                  }
                >
                  <i className="bi bi-box-arrow-right me-2" />
                  {isLoggingOut ? "Logging out…" : "Logout"}
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {showDropdown && (
        <div
          className="admin-header-scrim"
          onClick={() => setShowDropdown(false)}
        />
      )}

      <ProfileModal
        isOpen={showProfileModal}
        user={user}
        onClose={() => setShowProfileModal(false)}
        onEdit={() => {
          setShowProfileModal(false);
          setShowEditModal(true);
        }}
      />

      <EditProfileModal
        isOpen={showEditModal}
        user={user}
        onClose={() => setShowEditModal(false)}
        onSave={handleSaveProfile}
      />
    </header>
  );
};

export default Header;
